import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Select } from "antd";
import { my_routes } from "../router/all_routes";
import CommonSelect from "../../core/common/commonSelect";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// importations liées aux années universitaire
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { getAllAcademicYear } from "../../api/generalParam";

// importations liées aux classes
import { getAllClass } from "../../api/pedagogicElement";

// importation liées aux parents
import { getAllParents } from "../../api/parents";

// importation lées aux liens
import { getAllTypeLink } from "../../api/studentParent";

// importation liées aux inscription/etudiant
import { registerStudent } from "../../api/etudiants";

import { toast } from "sonner";
import InfosAlertYearDisabled from "../../core/common/alerts/infosAlertYearDisabled";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { getAllParcours } from "../../api/settings";
import StudentGuardiansSection, {
  GuardianDraft,
} from "./StudentGuardiansSection";

enum Sexe {
  Masculin = "Masculin",
  Feminin = "Feminin",
}

const status = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

const sexe = [
  { value: "Masculin", label: "Masculin" },
  { value: "Feminin", label: "Feminin" },
];

// Shema du formulaire pour enregistré l'insription de l'étudiant
const studentShema = z.object({
  academic_year: z
    .object(
      {
        value: z.string(),
        label: z.string(),
      },
      {
        required_error: "Veuillez choisir une année universitaire valide",
        invalid_type_error: "Veuillez choisir une année universitaire valide",
      },
    )
    .refine(
      (val) => val && val.value && val.label,
      "veuillez choisir une année universitaire valide",
    ),
  lastname: z.string().nonempty("Veuillez insérer un nom"),
  // classe doit accepter un objet CommonSelect { value: string, label: string, ... }
  classe: z
    .object(
      {
        value: z.string(),
        label: z.string(),
      },
      {
        required_error: "Veuillez choisir une classe valide",
        invalid_type_error: "Veuillez choisir une classe valide",
      },
    )
    .refine(
      (val) => val && val.value && val.label,
      "veuillez choisir une classe valide",
    ),
  id_parcours: z.string().nonempty("Veuillez choisir un parcours valide"),
  firstname: z.string().nonempty("Veuillez insérer un prénom"),
  sexe: z
    .object(
      {
        value: z.string(),
        label: z.string(),
      },
      {
        required_error: "Veuillez choisir un genre valide",
        invalid_type_error: "Veuillez choisir un genre valide",
      },
    )
    .refine(
      (val) => val && val.value && val.label,
      "veuillez choisir un genre valide",
    ),
  birthday: z.any().optional(),
  email: z.string().optional(),
  number: z.string().optional(),
  adresse: z.string().optional(),
  place_birth: z.string(),
  statut: z.enum(["true", "false"], {
    required_error: "Veuillez sélectionner un statut",
  }),
});
type RegisterFormFields = z.infer<typeof studentShema>;

// Schema de notre formulaire de création de compte parent
const schema = z.object({
  lastname: z.string().nonempty("Veuillez insérer un nom"),
  firstname: z.string().nonempty("Veuillez insérer un prénom"),
  sexe: z.nativeEnum(Sexe, {
    errorMap: () => ({ message: "Veuillez sélectionner un type valide." }),
  }),
  isDeceased: z.boolean(),
  email: z
    .string()
    // .email("Veuillez fournir une adresse email valide")
    .optional(),
  number: z
    .string()
    // .regex(
    //   /^(\+223)?[456789]\d{7}$/,
    //   "Numéro de téléphone invalide. Ex: +22370123456 ou 70123456"
    // )
    .optional(),
  adresse: z.string().optional(),
  statut: z.enum(["true", "false"], {
    required_error: "Veuillez sélectionner un statut",
  }),
  relation: z.any(),
});

type FormFields = z.infer<typeof schema>;

//  shéma pour la vérification des parents
const parentShema = z.object({
  id: z.string(),
  value: z.string(),
  label: z.string(),
  relation: z
    .object({
      id: z.string(),
      label: z.string(),
    })
    .nullable()
    .refine((val) => val !== null && val !== undefined, {
      message: "La relation est obligatoire",
    }),
});

const parentsArrayShema = z.array(parentShema);

const StudentRegistration = () => {
  const routes = my_routes;
  const navigate = useNavigate();
  const parentSelectorRef = useRef(null);
  // Modal détails
  const [show, setShow] = useState(false);
  const handleClose = () => {
    setShow(false);
  };

  const { selectedYear } = useAcademicYear();
  const [allClass, setAllClass] = useState<any[]>([]);
  const [allParents, setAllParent] = useState<any[]>([]);
  const [allAcademicYear, setAllAcademicYear] = useState<any[]>([]);
  const [allTypeLink, setAllTypeLink] = useState<
    { id: string; label: string }[]
  >([]);
  // pour le select
  const [assignedParent, setAssignedParent] = useState<any[]>([]);

  // pour le lien avec un parent déjà existant
  const [existentParentToLink, setExistentParentToLink] = useState<any[]>([]);
  const [guardians, setGuardians] = useState<GuardianDraft[]>([]);
  const [defaultLinkType, setDefaultLinkType] = useState<{
    id: string;
    label: string;
  }>();

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [studentEdited, setStudentEdited] = useState<any>();

  const [allParcours, setAllParcours] = useState<any[]>([]);

  //   Initialisation de zod avec react-hook-form pour le compte parent
  const {
    setError: parentSetError,
    reset: parentReset,
    clearErrors: parentClearErrors,
  } = useForm<FormFields>({
    resolver: zodResolver(schema),
  });

  // Iniatialisation de zod avec react-hook-form pour l'inscription /edition
  const {
    register: inscriptionRegister,
    handleSubmit: inscriptionHandleSubmit,
    setValue: inscriptionSetValue,
    watch: inscriptionWatch,
    clearErrors: inscriptionClearErrors,
    formState: {
      errors: inscriptionErrors,
      isSubmitting: inscriptionIsSubmitting,
    },
  } = useForm<RegisterFormFields>({
    resolver: zodResolver(studentShema),
    defaultValues: {
      statut: "true",
    },
  });

  const academic_year = inscriptionWatch("academic_year");

  useEffect(() => {
    inscriptionSetValue("academic_year", {
      value: selectedYear?.id ?? "",
      label: selectedYear?.label ?? "",
    });
  }, [selectedYear]);

  useEffect(() => {
    inscriptionSetValue("academic_year", {
      label: selectedYear?.label ?? allAcademicYear[0].label,
      value: selectedYear?.id ?? allAcademicYear[0].value,
    });
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const response = await getAllAcademicYear();
      // console.log("all years: ", response.data);
      const formattedYears = response.data.map((year: any) => ({
        value: year.id,
        label: year.label,
      }));
      setAllAcademicYear(formattedYears);
    } catch (error: any) {
      // console.log(
      //   "erreur lors de la récupération des année academique: ",
      //   error,
      // );
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const fetchLinks = async () => {
    try {
      const response = await getAllTypeLink(
        buildPermissionContext(Resource.STUDENT, Action.CREATE),
      );
      const formattedLink = response.data.map((link: any) => ({
        id: link.id,
        label: link.type,
      }));
      // console.log("all link: ", response.data);
      setAllTypeLink(formattedLink);
    } catch (error: any) {
      toast.error(
        error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };
  const fetchClass = async () => {
    try {
      const year_id = academic_year?.value ?? selectedYear?.id;
      const response = await getAllClass(
        year_id,
        buildPermissionContext(Resource.STUDENT, Action.CREATE),
      );

      const matchedParcours = allParcours.find(
        (p: any) => p?.value === inscriptionWatch("id_parcours"),
      );

      const filteredResponse = matchedParcours
        ? response.data.filter((c: any) =>
            matchedParcours?.Cycle?.TypeLevels.some(
              (l: any) => l?.id === c?.Level?.id_typeLevel,
            ),
          )
        : response.data;

      const formattedData = filteredResponse
        .slice() // pour éviter de modifier directement la réponse
        .sort((a: any, b: any) => {
          // Tri par Level.label
          const levelCompare = a.Level.label.localeCompare(
            b.Level.label,
            "fr",
            { numeric: true },
          );
          if (levelCompare !== 0) return levelCompare;

          // Si même Level, tri par class label
          return a.label.localeCompare(b.label, "fr", { numeric: true });
        })
        .map((classItem: any) => ({
          value: classItem.id,
          label: `${classItem.Level.label} - ${classItem.label}`,
        }));

      setAllClass(formattedData);
    } catch (error: any) {
      // console.log("erreur lors de la récupération des classes: ", error);
      toast.error(
        error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };
  const fetchParents = async () => {
    try {
      const response = await getAllParents(
        buildPermissionContext(Resource.STUDENT, Action.CREATE),
      );
      // console.log("all parents: ", response.data);
      const formattedParents = response.data.map((parent: any) => ({
        ...parent,
        id: parent.id,
        value: parent.id,
        label: `${parent.firstname} ${parent.lastname} ${"    "} ${
          parent?.number ? parent?.number : ""
        } ${parent?.email || ""}`,
      }));
      setAllParent(formattedParents);
    } catch (error: any) {
      toast.error(
        error.message ||
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!isEdit) {
      fetchAcademicYears();

      fetchLinks();
    }
  }, [academic_year]);

  useEffect(() => {
    fetchClass();
  }, [academic_year, inscriptionWatch("id_parcours")]);
  // fonction pour recuperer tous les parcours
  const fetchAllParcours = async () => {
    try {
      const res = await getAllParcours(
        buildPermissionContext(Resource.STUDENT, Action.CREATE),
      );
      // console.log("Response all parcours:", res.data);

      const formattedParcours = res.data?.map((p: any) => {
          // console.log("Parcours: ", p);
          return {
            label: `${p.Cycle.label} ${p.title}`,
            Cycle: p?.Cycle,
            value: p.id,
          };
        })
        .sort((a: any, b: any) =>
          a.label.localeCompare(b.label, "fr", { numeric: true }),
        );

      if (formattedParcours.length > 0 && !inscriptionWatch("id_parcours"))
        inscriptionSetValue("id_parcours", formattedParcours[0].value);
      setAllParcours(formattedParcours ?? []);
    } catch (error: any) {
      // console.log("Error lors de la recuperation des parcours : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // useEffect pour charger ces parcours
  useEffect(() => {
    fetchAllParcours();
  },[]);

  // soumettre l'inscription
  const onSubmitRegister: SubmitHandler<RegisterFormFields> = async (data) => {
    try {
      const formData = {
        ...data,
        statut: data.statut === "true",
        email: data.email === "" ? undefined : data.email,
        sexe: data.sexe.value,
        id_academicYear: data.academic_year.value,
        id_class: data.classe.value,
        id_parent: undefined,
        id_link: undefined,
        guardians: guardians.map((guardian) => ({
          parentId: guardian.parentId,
          newParent: guardian.newParent,
          linkId: guardian.linkId,
        })),
      };
      const response = await registerStudent(formData);
      if (!response) throw new Error("L'inscription n'a pas pu être enregistrée");
      toast.success("Inscription Réussi");
      navigate(routes.allStudent);
    } catch (error: any) {
      // Gérer les erreurs de l'API
      // console.log(error);
      parentSetError("root", {
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "une erreur inattendu c'est produite veuillez ésssyez plus tard",
      });
    }
  };

  // Fonction pour les liens avec un parent déja existant
  const handleCreateLink = () => {
    // vérifie les enfants sélectionnés
    const validation = parentsArrayShema.safeParse(assignedParent);

    if (!validation.success) {
      // parentSetError("root", { message: errorMessage });
      toast.error(
        "Le format du parent est invalide ou une relation manquante.",
      );
      return;
    }
    // console.log("existent parent: ", existentParentToLink);
    // obligé un compte parent de type mere ou père
    if (existentParentToLink.length >= 1) {
      const exist = existentParentToLink.find(
        (parent) =>
          parent.relation.label.toLowerCase() !== "pere" &&
          parent.relation.label.toLowerCase() !== "mere",
      );

      if (
        exist &&
        assignedParent[0].relation.label.toLowerCase() !== "pere" &&
        assignedParent[0].relation.label.toLowerCase() !== "mere"
      ) {
        toast.info("Un seul parent peut être différent du Pere et de la Mere");
        return;
      }
    }

    setExistentParentToLink((prev) => {
      const exists = prev.some(
        (parent) =>
          parent.relation &&
          assignedParent[0].relation &&
          parent.relation.id === assignedParent[0].relation.id,
      );
      if (exists) {
        return prev.map((parent) =>
          parent.relation &&
          assignedParent[0].relation &&
          parent.relation.id === assignedParent[0].relation.id
            ? assignedParent[0]
            : parent,
        );
      } else {
        // limité le nombre de parent au nombre de role
        if (prev.length >= 3) {
          toast.info("Vous avez déja attibuer tous les rôles disponible");
          return prev;
        }
        return [...prev, assignedParent[0]];
      }
    });
    const btn = document.getElementById("cancel-btn");
    if (btn) btn.click();
  };

  // Fonction pour gérer la sélection des parents
  const handleParentChange = (
    selectedIds: string[],
    option?:
      | {
          id: string;
          value: string;
          label: string;
          relation: { id: string; label: string } | null;
        }
      | {
          id: string;
          value: string;
          label: string;
          relation: { id: string; label: string } | null;
        }[]
      | undefined,
  ) => {
    // option peut être undefined ou un tableau ou un objet, donc on doit gérer les cas
    let selectedOptions: {
      id: string;
      value: string;
      label: string;
      relation: { id: string; label: string } | null;
    }[] = [];
    if (option) {
      selectedOptions = [
        option as {
          id: string;
          value: string;
          label: string;
          relation: { id: string; label: string } | null;
        },
      ];
    } else {
      // fallback: reconstruire à partir des valeurs sélectionnées
      selectedOptions = allParents.filter((parent) =>
        selectedIds.includes(parent.id),
      );
    }
    // Ajoute la propriété relation: null par défaut si elle n'existe pas
    const newSelectedParent = selectedOptions.map((child) => ({
      ...child,
      relation: child.relation ?? defaultLinkType ?? null,
    }));
    setAssignedParent(newSelectedParent);
  };

  // Pour changer la relation avec un parent
  const updateRelation = (parentId: string, relationLabel: string) => {
    const matchedLink = allTypeLink.find(
      (link) => link.label === relationLabel,
    );

    if (!matchedLink) {
      console.warn(`Aucune relation trouvée pour ce label : ${relationLabel}`);
      toast.error(`Aucune relation trouvée pour ce label : ${relationLabel}`);
      return;
    }

    setAssignedParent((prev) =>
      prev.map((parent) =>
        parent.id === parentId ? { ...parent, relation: matchedLink } : parent,
      ),
    );
  };

  return (
    <>
      {/* Page wrapper */}
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">
                {isEdit ? "Modifier" : "Ajouter"} Etudiant
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.allStudent}>Etudiants</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Modifier" : "Ajouter"} Etudiant
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}
          {!selectedYear?.statut && <InfosAlertYearDisabled />}

          <div className="row">
            <div className="col-md-12">
              <form onSubmit={inscriptionHandleSubmit(onSubmitRegister)}>
                {/* Information personnel */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-info-square-rounded fs-16" />
                      </span>
                      <h4 className="text-dark">Information Personnel</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                          <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                            <i className="ti ti-photo-plus fs-16" />
                          </div>
                          <div className="profile-upload">
                            <div className="profile-uploader d-flex align-items-center">
                              <div className="drag-upload-btn mb-3">
                                Upload
                                <input
                                  type="file"
                                  className="form-control image-sign"
                                  multiple
                                />
                              </div>
                              <Link to="#" className="btn btn-primary mb-3">
                                Remove
                              </Link>
                            </div>
                            <p className="fs-12">
                              Upload image size 4MB, Format JPG, PNG, SVG
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row row-cols-xxl-5 row-cols-md-6">
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Prénom</label>
                          <input
                            type="text"
                            placeholder="Entrer le prénom"
                            className={
                              inscriptionErrors.firstname
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={
                              isEdit ? studentEdited?.firstname : undefined
                            }
                            {...inscriptionRegister("firstname")}
                          />
                          {inscriptionErrors.firstname && (
                            <div className="invalid-feedback">
                              {inscriptionErrors?.firstname.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Nom</label>
                          <input
                            type="text"
                            placeholder="Entrer le nom"
                            className={
                              inscriptionErrors.lastname
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={
                              isEdit ? studentEdited?.lastname : undefined
                            }
                            {...inscriptionRegister("lastname")}
                          />
                          {inscriptionErrors.lastname && (
                            <div className="invalid-feedback">
                              {inscriptionErrors.lastname.message}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Genre</label>
                          <CommonSelect
                            className={
                              inscriptionErrors.sexe
                                ? "select is-invalid"
                                : "select"
                            }
                            onChange={(option: any) => {
                              inscriptionSetValue("sexe", option || "");
                              inscriptionClearErrors("sexe");
                            }}
                            options={sexe}
                            defaultValue={undefined}
                          />
                          {inscriptionErrors.sexe && (
                            <div className="invalid-feedback">
                              {inscriptionErrors.sexe.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Date d'anniversaire
                          </label>
                          <div className="input-icon position-relative">
                            <input
                              type="date"
                              className={
                                inscriptionErrors.birthday
                                  ? "form-control datetimepicker is-invalid"
                                  : "form-control datetimepicker"
                              }
                              placeholder="DD-MM-YYYY"
                              {...inscriptionRegister("birthday")}
                            />

                            {inscriptionErrors.birthday && (
                              <div className="invalid-feedback">
                                {inscriptionErrors.birthday &&
                                typeof inscriptionErrors.birthday.message ===
                                  "string"
                                  ? inscriptionErrors.birthday.message
                                  : null}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Lieu de naissance
                          </label>
                          <input
                            type="text"
                            placeholder="Entrer le lieu"
                            className={
                              inscriptionErrors.place_birth
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={
                              isEdit ? studentEdited?.place_birth : undefined
                            }
                            {...inscriptionRegister("place_birth")}
                          />
                          {inscriptionErrors.place_birth && (
                            <div className="invalid-feedback">
                              {inscriptionErrors.place_birth.message}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Numéro de Téléphone:
                          </label>
                          <input
                            type="text"
                            placeholder="Entrer un numéro de Tel"
                            className={
                              inscriptionErrors.number
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={
                              isEdit ? studentEdited?.number : undefined
                            }
                            {...inscriptionRegister("number")}
                          />
                          {inscriptionErrors.number && (
                            <div className="invalid-feedback">
                              {inscriptionErrors.number.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Adresse Mail</label>
                          <input
                            type="email"
                            placeholder="Entrer une adresse mail"
                            className={
                              inscriptionErrors.email
                                ? "form-control  is-invalid"
                                : "form-control "
                            }
                            defaultValue={
                              isEdit ? (studentEdited?.email ?? "") : ""
                            }
                            {...inscriptionRegister("email")}
                          />
                          {inscriptionErrors.email && (
                            <div className="invalid-feedback">
                              {inscriptionErrors.email.message}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Status</label>
                          <CommonSelect
                            className={
                              inscriptionErrors.statut
                                ? "select is-invalid"
                                : "select"
                            }
                            options={status}
                            defaultValue={status[0]}
                            onChange={(option: any) => {
                              inscriptionSetValue(
                                "statut",
                                option?.value || "",
                              );
                              inscriptionClearErrors("statut");
                            }}
                          />
                          {inscriptionErrors.statut && (
                            <div className="invalid-feedback">
                              {inscriptionErrors.statut.message}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-xxl-12 col-md-12 col-xl-12">
                        <div className="mb-3">
                          <label className="form-label">Adresse Physique</label>
                          <textarea
                            {...inscriptionRegister("adresse")}
                            placeholder="Exemple : Badalabougou, Commune 5, Rue 300 Porte 218"
                            rows={4}
                            cols={8}
                            className={
                              inscriptionErrors.adresse
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                          />
                          {inscriptionErrors.adresse && (
                            <div className="invalid-feedback">
                              {inscriptionErrors.adresse.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Information personnel */}

                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-road fs-16" />
                      </span>
                      <h4 className="text-dark">Parcours Universitaire</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row row-cols-xxl-2 row-cols-md-2">
                      {/*selecteur parcours Universitaire*/}
                      <div className="col-xxl col-xl-3 col-md-6">
                        <label htmlFor="" className="form-label">
                          Selectionné un Parcours universitaire
                        </label>
                        <br />
                        <Select
                          id="parcours-selector"
                          // mode="multiple"
                          //   allowClear
                          className={`select ${inscriptionErrors.id_parcours ? "is-invalid" : ""}`}
                          size="large"
                          style={{ width: "100%" }}
                          placeholder="Selectionner un parcours"
                          defaultValue={undefined}
                          value={inscriptionWatch("id_parcours") ?? undefined}
                          options={allParcours}
                          onChange={(option) => {
                            // console.log("selected Parcours: ", option);
                            if (option)
                              inscriptionSetValue("id_parcours", option);
                            inscriptionClearErrors("id_parcours");
                          }}
                          // Permet de chercher par label a lieu de l'id
                          filterOption={(input: any, option: any) =>
                            (option?.label ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        />
                        {inscriptionErrors.id_parcours && (
                          <div className="invalid-feedback">
                            {inscriptionErrors.id_parcours?.message}
                          </div>
                        )}
                      </div>

                      {/*selecteur de classes*/}
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Classe</label>
                          <CommonSelect
                            className={
                              inscriptionErrors.classe
                                ? "select is-invalid"
                                : "select"
                            }
                            options={allClass}
                            defaultValue={isEdit ? allClass[0] : undefined}
                            onChange={(option) => {
                              if (option) {
                                inscriptionSetValue("classe", option);
                                inscriptionClearErrors("classe");
                              }
                            }}
                          />
                          {inscriptionErrors.classe && (
                            <div className="invalid-feedback">
                              {inscriptionErrors.classe.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Information Parent et Garde  */}
                <StudentGuardiansSection
                  guardians={guardians}
                  linkTypes={allTypeLink}
                  onChange={setGuardians}
                />
                <div className="card d-none" aria-hidden="true">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-user-shield fs-16" />
                      </span>
                      <h4 className="text-dark">Lien Parents &amp; Garde</h4>
                    </div>
                  </div>
                  <div className="card-body pb-0">
                    <div className="border-bottom mb-3">
                      <div className="d-flex justify-content-between">
                        <h5 className="mb-3">Infos Père</h5>
                        <div className="dropdown">
                          <Link
                            to="#"
                            // className="dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <i className="ti ti-dots-vertical fs-14" />
                          </Link>
                          <div className="dropdown-menu dropdown-menu-end">
                            <button
                              type="button"
                              disabled={
                                // On cherche le parent "Mere" dans existentParentToLink
                                existentParentToLink.find(
                                  (parent) =>
                                    parent.relation &&
                                    parent.relation.label &&
                                    parent.relation.label.toLowerCase() ===
                                      "pere",
                                )
                              }
                              className="dropdown-item"
                              onClick={() => {
                                setShow(true);
                                setDefaultLinkType(
                                  allTypeLink.find(
                                    (link) =>
                                      link.label.toLowerCase() === "pere",
                                  ),
                                );
                              }}
                            >
                              Créer Lien
                            </button>
                            <button
                              type="button"
                              disabled={
                                // On cherche le parent "Pere" dans existentParentToLink
                                !existentParentToLink.find(
                                  (parent) =>
                                    parent.relation &&
                                    parent.relation.label &&
                                    parent.relation.label.toLowerCase() ===
                                      "pere",
                                )
                              }
                              className="dropdown-item"
                              onClick={() => {
                                // On exclus le parent "Pere" dans existentParentToLink
                                setExistentParentToLink((prev) =>
                                  prev.filter(
                                    (parent) =>
                                      parent.relation &&
                                      parent.relation.label &&
                                      parent.relation.label.toLowerCase() !==
                                        "pere",
                                  ),
                                );
                              }}
                            >
                              Supprimer Lien
                            </button>
                          </div>
                        </div>
                      </div>
                      {existentParentToLink.length > 0 &&
                        (() => {
                          // On cherche le parent "Père" dans existentParentToLink
                          const pereLink = existentParentToLink.find(
                            (parent) =>
                              parent.relation &&
                              parent.relation.label &&
                              parent.relation.label.toLowerCase() === "pere",
                          );
                          if (!pereLink) return null;
                          // On cherche les infos du parent dans allParents
                          const pereInfo = allParents.find(
                            (p) => p.id === pereLink.id,
                          );
                          return (
                            <div className="row">
                              <div className="col-md-12">
                                <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                                  <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                                    <i className="ti ti-photo-plus fs-16" />
                                  </div>
                                  <div className="profile-upload">
                                    <div className="profile-uploader d-flex align-items-center">
                                      <div className="drag-upload-btn mb-3">
                                        Upload
                                        <input
                                          type="file"
                                          className="form-control image-sign"
                                          multiple
                                        />
                                      </div>
                                      <Link
                                        to="#"
                                        className="btn btn-primary mb-3"
                                      >
                                        Remove
                                      </Link>
                                    </div>
                                    <p className="fs-12">
                                      Upload image size 4MB, Format JPG, PNG,
                                      SVG
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="col-lg-3 col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">Nom Père</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={pereInfo?.label || ""}
                                    readOnly
                                  />
                                </div>
                              </div>
                              <div className="col-lg-3 col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">Email</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={pereInfo?.email || ""}
                                    readOnly
                                  />
                                </div>
                              </div>
                              <div className="col-lg-3 col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Numéro Téléphone
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={pereInfo?.number || ""}
                                    readOnly
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                    </div>

                    <div className="border-bottom mb-3">
                      <div className="d-flex justify-content-between">
                        <h5 className="mb-3">Infos Mère</h5>
                        <div className="dropdown">
                          <Link
                            to="#"
                            // className="dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <i className="ti ti-dots-vertical fs-14" />
                          </Link>
                          <div className="dropdown-menu dropdown-menu-end">
                            <button
                              type="button"
                              disabled={
                                // On cherche le parent "Mere" dans existentParentToLink
                                existentParentToLink.find(
                                  (parent) =>
                                    parent.relation &&
                                    parent.relation.label &&
                                    parent.relation.label.toLowerCase() ===
                                      "mere",
                                )
                              }
                              className="dropdown-item"
                              onClick={() => {
                                setShow(true);
                                setDefaultLinkType(
                                  allTypeLink.find(
                                    (link) =>
                                      link.label.toLowerCase() === "mere",
                                  ),
                                );
                              }}
                            >
                              Créer Lien
                            </button>
                            <button
                              type="button"
                              disabled={
                                // On cherche le parent "Mere" dans existentParentToLink
                                !existentParentToLink.find(
                                  (parent) =>
                                    parent.relation &&
                                    parent.relation.label &&
                                    parent.relation.label.toLowerCase() ===
                                      "mere",
                                )
                              }
                              className="dropdown-item"
                              onClick={() => {
                                // On exclus le parent "Mere" dans existentParentToLink
                                setExistentParentToLink((prev) =>
                                  prev.filter(
                                    (parent) =>
                                      parent.relation &&
                                      parent.relation.label &&
                                      parent.relation.label.toLowerCase() !==
                                        "mere",
                                  ),
                                );
                              }}
                            >
                              Supprimer Lien
                            </button>
                          </div>
                        </div>
                      </div>
                      {existentParentToLink.length > 0 &&
                        (() => {
                          // On cherche le parent "Mère" dans existentParentToLink
                          const mereLink = existentParentToLink.find(
                            (parent) =>
                              parent.relation &&
                              parent.relation.label &&
                              parent.relation.label.toLowerCase() === "mere",
                          );
                          if (!mereLink) return null;
                          // On cherche les infos du parent dans allParents
                          const mereInfo = allParents.find(
                            (p) => p.id === mereLink.id,
                          );
                          return (
                            <div className="row">
                              <div className="col-md-12">
                                <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                                  <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                                    <i className="ti ti-photo-plus fs-16" />
                                  </div>
                                  <div className="profile-upload">
                                    <div className="profile-uploader d-flex align-items-center">
                                      <div className="drag-upload-btn mb-3">
                                        Upload
                                        <input
                                          type="file"
                                          className="form-control image-sign"
                                          multiple
                                        />
                                      </div>
                                      <Link
                                        to="#"
                                        className="btn btn-primary mb-3"
                                      >
                                        Remove
                                      </Link>
                                    </div>
                                    <p className="fs-12">
                                      Upload image size 4MB, Format JPG, PNG,
                                      SVG
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="col-lg-3 col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">Nom Mère</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={mereInfo?.label || ""}
                                    readOnly
                                  />
                                </div>
                              </div>
                              <div className="col-lg-3 col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">Email</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={mereInfo?.email || ""}
                                    readOnly
                                  />
                                </div>
                              </div>
                              <div className="col-lg-3 col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Numéro téléphone
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={mereInfo?.number || ""}
                                    readOnly
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                    </div>
                    <div>
                      <div className="d-flex justify-content-between">
                        <h5 className="mb-3">Autre Tuteur</h5>
                        <div className="dropdown">
                          <Link
                            to="#"
                            // className="dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <i className="ti ti-dots-vertical fs-14" />
                          </Link>
                          <div className="dropdown-menu dropdown-menu-end">
                            <button
                              type="button"
                              disabled={
                                // On cherche le parent "Tuteur" dans existentParentToLink
                                existentParentToLink.find(
                                  (parent) =>
                                    parent.relation &&
                                    parent.relation.label &&
                                    parent.relation.label.toLowerCase() !==
                                      "mere" &&
                                    parent.relation.label.toLowerCase() !==
                                      "pere",
                                )
                              }
                              className="dropdown-item"
                              onClick={() => {
                                setShow(true);
                                setDefaultLinkType(undefined);
                              }}
                            >
                              Créer Lien
                            </button>
                            <button
                              type="button"
                              disabled={
                                !existentParentToLink.find(
                                  (parent) =>
                                    parent.relation &&
                                    parent.relation.label &&
                                    parent.relation.label.toLowerCase() !==
                                      "mere" &&
                                    parent.relation.label.toLowerCase() !==
                                      "pere",
                                )
                              }
                              className="dropdown-item"
                              onClick={() => {
                                // On exclus le parent "Tuteur" dans existentParentToLink
                                setExistentParentToLink((prev) =>
                                  prev.filter(
                                    (parent) =>
                                      (parent.relation &&
                                        parent.relation.label &&
                                        parent.relation.label.toLowerCase() ===
                                          "mere") ||
                                      parent.relation.label.toLowerCase() ===
                                        "pere",
                                  ),
                                );
                              }}
                            >
                              Annuler Lien
                            </button>
                          </div>
                        </div>
                      </div>
                      {existentParentToLink.length > 0 &&
                        (() => {
                          // On cherche le parent "Tuteur" dans existentParentToLink
                          const tuteurLink = existentParentToLink.find(
                            (parent) =>
                              parent.relation &&
                              parent.relation.label &&
                              parent.relation.label.toLowerCase() !== "mere" &&
                              parent.relation.label.toLowerCase() !== "pere",
                          );
                          if (!tuteurLink) return null;
                          // On cherche les infos du parent dans allParents
                          const tuteurInfo = allParents.find(
                            (p) => p.id === tuteurLink.id,
                          );
                          return (
                            <div className="row">
                              <div className="col-md-12">
                                <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                                  <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                                    <i className="ti ti-photo-plus fs-16" />
                                  </div>
                                  <div className="profile-upload">
                                    <div className="profile-uploader d-flex align-items-center">
                                      <div className="drag-upload-btn mb-3">
                                        Upload
                                        <input
                                          type="file"
                                          className="form-control image-sign"
                                          multiple
                                        />
                                      </div>
                                      <Link
                                        to="#"
                                        className="btn btn-primary mb-3"
                                      >
                                        Remove
                                      </Link>
                                    </div>
                                    <p className="fs-12">
                                      Upload image size 4MB, Format JPG, PNG,
                                      SVG
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="col-lg-3 col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Nom du Guarde
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={tuteurInfo?.label || ""}
                                    readOnly
                                  />
                                </div>
                              </div>
                              <div className="col-lg-3 col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Relation avec Garde
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={tuteurLink?.relation.label || ""}
                                    readOnly
                                  />
                                </div>
                              </div>
                              <div className="col-lg-3 col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">
                                    Numéro téléphone
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={tuteurInfo?.number || ""}
                                    readOnly
                                  />
                                </div>
                              </div>
                              <div className="col-lg-3 col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">Email</label>
                                  <input
                                    type="email"
                                    className="form-control"
                                    value={tuteurInfo?.eamail || ""}
                                    readOnly
                                  />
                                </div>
                              </div>
                              <div className="col-lg-3 col-md-6">
                                <div className="mb-3">
                                  <label className="form-label">Adresse</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={tuteurInfo?.adresse || ""}
                                    readOnly
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                    </div>
                  </div>
                </div>
                {/* Information Parent et Garde  */}

                <div className="text-end">
                  <Link
                    to={routes.allStudent}
                    type="button"
                    className="btn btn-light me-3"
                  >
                    Annuler
                  </Link>
                  {inscriptionIsSubmitting ? (
                    <button
                      className="btn btn-primary-light"
                      type="button"
                      disabled={true}
                    >
                      <span
                        className="spinner-border spinner-border-sm align-middle me-1 "
                        role="status"
                        aria-hidden="true"
                      />
                      Loading...
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="btn btn-primary"
                    >
                      Ajouter Etudiant
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* /Page wrapper */}
      {/* Modal  Add Parent*/}
      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="lg"
        id="add-parent-for-student"
      >
        <div className="modal-header">
          <h4 className="modal-title">Assigner Parent</h4>
          <button
            type="button"
            className="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={handleClose}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="modal-body pb-0">
          {/* Selection des parents */}
          <div
            ref={parentSelectorRef}
            className="mb-12 flex-fill "
            id="parent-selector"
          >
            <label htmlFor="" className="form-label">
              Selectionné un Parent existant
            </label>
            <br />
            <Select
              id="parent-selector"
              // mode="multiple"
              // allowClear
              className="select"
              getPopupContainer={() =>
                parentSelectorRef.current || document.body
              }
              style={{
                maxWidth: "500px",
                width: "100%",
                color: "white",
              }}
              placeholder="Please select"
              defaultValue={[]}
              value={assignedParent.map((parent) => parent.id)}
              options={allParents.filter(
                (p) =>
                  !existentParentToLink.some(
                    (assigned) => assigned.id === p.id,
                  ),
              )}
              onChange={handleParentChange}
              // Permet de chercher par nom/prenom a lieu de l'id
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
            {assignedParent.length > 0 && (
              <div className="mt-2">
                <h6>Parent sélectionné :</h6>
                <ul className="list-group">
                  {assignedParent.map((parent) => (
                    <li
                      key={parent.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <span>{parent.label}</span>
                      <select
                        name=""
                        id=""
                        className="form-select ms-2"
                        style={{ maxWidth: "200px" }}
                        value={
                          defaultLinkType?.label ?? parent.relation?.label ?? ""
                        }
                        onChange={(e) =>
                          updateRelation(parent.id, e.target.value)
                        }
                      >
                        <option value="">Choisir une relation</option>
                        {allTypeLink.map((link) => {
                          const isUsed = existentParentToLink.some(
                            (assigned) =>
                              assigned.relation?.label === link.label &&
                              assigned.id !== parent.id, // sauf pour le parent actuel
                          );
                          return (
                            <option
                              key={link.id}
                              value={link.label}
                              disabled={isUsed}
                              // selected={defaultLinkType?.id===link.id?true:false}
                            >
                              {link.label}
                            </option>
                          );
                        })}
                      </select>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-4 mb-3 d-flex justify-content-end mr-8">
              <Link
                to="#"
                id="cancel-btn"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
                onClick={() => {
                  parentReset();
                  parentClearErrors();
                  setAssignedParent([]);
                  setShow(false);
                }}
              >
                Annuler
              </Link>
              <button
                onClick={handleCreateLink}
                className="btn btn-outline-secondary "
              >
                Créer Lien
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default StudentRegistration;
